import { Duration } from 'luxon';
import { CycleBillingTerms } from '../../../clients/quipli-service/order/order';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { Field, FieldLabel } from '../../../components/field/field';
import { Button } from '../../../components/form/button';
import { DurationInput } from '../../../components/form/duration-input';
import Icon from '../../../components/icons/icon';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { Tooltip } from '../../../components/tooltip/tooltip';
import { Typography } from '../../../components/typography/typography';
import { addNumericSuffix } from '../../../util/util';
import { User } from '../../../clients/quipli-service/user/user';
import { useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Divider } from '../../../components/divider/divider';
import { toast } from '../../../util/toasts';

type OrderCycleBillingTermsForm = {
  global: CycleBillingTerms;
  order: CycleBillingTerms;
  state: CycleBillingTerms;
};

export const OrderCycleBillingTermsForm = ({
  customer,
  hideCheckbox = false,
  isDisabled = false,
  isChecked,
  isEditing,
  cycleBillingTerms,
  disableSaveCustomerCycleBillingTerms = false,
  setIsChecked,
  setIsEditing,
  setCycleBillingTerms,
}: {
  customer?: User;
  hideCheckbox?: boolean;
  isDisabled?: boolean;
  isChecked: boolean;
  isEditing: boolean;
  cycleBillingTerms: OrderCycleBillingTermsForm;
  disableSaveCustomerCycleBillingTerms?: boolean;
  setIsChecked?: (isChecked: boolean) => void;
  setIsEditing: (isEditing: boolean) => void;
  setCycleBillingTerms: (cycleBillingTerms: OrderCycleBillingTermsForm) => void;
}) => {
  const { getUserCycleBillingTerms, updateUserCycleBillingTerms } = useAPI();

  const [isConfirmCustomerTermsOpen, setIsConfirmCustomerTermsOpen] = useState<boolean>(false);

  const saveCustomerCycleBillingTerms = async () => {
    if (!customer || disableSaveCustomerCycleBillingTerms) return;

    try {
      const updatedCycleBillingTerms = await updateUserCycleBillingTerms(customer.id, cycleBillingTerms.state);
      toast('Cycle billing terms updated successfully', 'success');
      setCycleBillingTerms({ ...cycleBillingTerms, order: updatedCycleBillingTerms });
      setIsConfirmCustomerTermsOpen(false);
      setIsEditing(false);
    } catch (error: any) {
      toast(error.message, 'error');
    }
  };

  useEffect(() => {
    if (customer) {
      getUserCycleBillingTerms(customer.id).then((cbt) => {
        if (cbt) {
          setIsEditing(false);
          setCycleBillingTerms({ ...cycleBillingTerms, order: cbt, state: cbt });
        }
      });
    }
  }, [customer]);

  return (
    <>
      <div className="flex flex-col gap-[12px]">
        {!hideCheckbox && (
          <div className="w-fit">
            <Tooltip tooltip="Remove all products to toggle cycle billing" invisible={!isDisabled} type="black">
              <Checkbox
                type="white"
                isChecked={isChecked}
                onChange={() => setIsChecked?.(!isChecked)}
                label={
                  <Typography type="text-md" className="text-grey-700">
                    Cycle billing
                  </Typography>
                }
                isDisabled={isDisabled}
                dataCy="cycle-billing-checkbox"
              />
            </Tooltip>
          </div>
        )}

        <div className="flex items-center">
          <Typography type="text-sm" className="text-grey-500">
            Cycle billing will enable recurring invoices every{' '}
            {cycleBillingTerms.order.cycleLength.toHuman() ?? '28 days'}. Invoice will send to renter on the{' '}
            {addNumericSuffix(
              cycleBillingTerms.order.cycleInvoiceDay ?? cycleBillingTerms.order.cycleLength.days ?? 28,
            )}{' '}
            day of the cycle.{' '}
          </Typography>
          {isChecked && (
            <Button className="button-tertiary pb-1 pl-1 ml-2">
              <Icon icon="Edit" onClick={() => setIsEditing(true)} dataCy="cycle-bill-edit" />
            </Button>
          )}
        </div>
      </div>

      {isChecked && cycleBillingTerms && isEditing && (
        <>
          <div className="flex flex-col gap-[12px]">
            <div className="w-fit">
              <div className="flex align-middle gap-8">
                <div className="flex items-end mt-3 gap-2">
                  <div>
                    <FieldLabel label="Cycle Bill Length" isRequired={true} />
                    <DurationInput
                      value={cycleBillingTerms.state.cycleLength}
                      onChange={(cycleLength) =>
                        setCycleBillingTerms({
                          ...cycleBillingTerms,
                          state: {
                            ...cycleBillingTerms.state,
                            cycleLength,
                          },
                        })
                      }
                      allowedUnits={['months', 'weeks', 'days']}
                    />
                  </div>
                </div>
                <div className="flex items-end mt-3 gap-2">
                  <Field
                    type="number"
                    placeholder="30"
                    label="Payment Terms"
                    isRequired={true}
                    value={cycleBillingTerms.state.paymentPeriod.days}
                    onChange={(paymentPeriod) =>
                      setCycleBillingTerms({
                        ...cycleBillingTerms,
                        state: {
                          ...cycleBillingTerms.state,
                          paymentPeriod: Duration.fromObject({ days: paymentPeriod }),
                        },
                      })
                    }
                    dataCy="payment-terms"
                  />
                  <Typography type="text-md" className="text-grey-500">
                    days
                  </Typography>
                </div>
              </div>
              <div className="flex items-end mt-3 gap-2">
                <Field
                  className="mt-3"
                  type="number"
                  placeholder="28"
                  label="Day of Cycle Invoice Sent to Customer"
                  isRequired={true}
                  value={cycleBillingTerms.state.cycleInvoiceDay}
                  onChange={(cycleInvoiceDay) =>
                    setCycleBillingTerms({
                      ...cycleBillingTerms,
                      state: { ...cycleBillingTerms.state, cycleInvoiceDay },
                    })
                  }
                  invalid={
                    cycleBillingTerms.state.cycleLength.months !== 1 &&
                    cycleBillingTerms.state.cycleLength
                      .minus({ days: cycleBillingTerms.state.cycleInvoiceDay })
                      .as('milliseconds') < 0
                  }
                  dataCy="cycle-invoice-day"
                />
                {cycleBillingTerms.state.cycleLength.months === 1 && (
                  <span className="py-2">
                    <Checkbox
                      type="white"
                      isChecked={cycleBillingTerms.state.isDayOfMonth}
                      onChange={() =>
                        setCycleBillingTerms({
                          ...cycleBillingTerms,
                          state: {
                            ...cycleBillingTerms.state,
                            isDayOfMonth: !cycleBillingTerms.state.isDayOfMonth,
                          },
                        })
                      }
                      label={
                        <Typography type="text-md" className="text-grey-700">
                          As Day of Month
                        </Typography>
                      }
                      dataCy="cycle-invoice-day-of-month"
                    />
                  </span>
                )}
              </div>
              {cycleBillingTerms.state.cycleLength.months !== 1 &&
                cycleBillingTerms.state.cycleLength
                  .minus({ days: cycleBillingTerms.state.cycleInvoiceDay })
                  .as('milliseconds') < 0 && (
                  <Typography type="text-xs" className="text-error-500">
                    * Invoice day cannot be greater than cycle length
                  </Typography>
                )}
            </div>
            <div className="flex justify-end gap-3">
              <Button
                className="button-tertiary"
                onClick={() => {
                  setCycleBillingTerms({ ...cycleBillingTerms, state: cycleBillingTerms.order });
                  setIsEditing(false);
                }}
                dataCy="cycle-bill-cancel"
              >
                Cancel
              </Button>
              <Button
                className="button-primary"
                onClick={() => {
                  if (customer && !disableSaveCustomerCycleBillingTerms) {
                    setIsConfirmCustomerTermsOpen(true);
                  } else {
                    setCycleBillingTerms({ ...cycleBillingTerms, order: cycleBillingTerms.state });
                    setIsEditing(false);
                  }
                }}
                disabled={
                  cycleBillingTerms.state.cycleLength.as('milliseconds') <= 0 ||
                  cycleBillingTerms.state.paymentPeriod.days < 0 ||
                  !cycleBillingTerms.state.cycleInvoiceDay ||
                  !(
                    cycleBillingTerms.state.cycleLength.months === 1 ||
                    cycleBillingTerms.state.cycleLength
                      .minus({ days: cycleBillingTerms.state.cycleInvoiceDay })
                      .as('milliseconds') >= 0
                  )
                }
                dataCy="cycle-bill-save"
              >
                Save
              </Button>
            </div>
          </div>

          <div className="pt-[12px]">
            <Divider />
          </div>
        </>
      )}

      {isConfirmCustomerTermsOpen && (
        <ConfirmModal
          isOpen
          close={() => {
            setCycleBillingTerms({ ...cycleBillingTerms, order: cycleBillingTerms.state });
            setIsConfirmCustomerTermsOpen(false);
            setIsEditing(false);
          }}
          onConfirm={() => saveCustomerCycleBillingTerms()}
          headerIcon={<Icon icon="HelpCircle" size="xlarge" stroke="var(--blue-gray-500)" />}
          prompt="Customer Cycle Billing Terms"
          additionalText="Would you like to apply these cycle billing terms to all future cycle bill orders for this customer?"
          noLabel="No, apply just this once"
          yesLabel="Yes, apply to all future cycle bill orders for this customer"
          yesDataCy="confirm-customer-cycle-billing-terms"
        />
      )}
    </>
  );
};
